/**
 * AiCopyModal — 시험지 [AI 복사] 시작 (계약 BE-20260825-exam-papers-screens.md §4·§5-3).
 *
 * 단건 변형(VariationTab)의 요청 바를 시험지 한 장에 통째로 거는 입구다. 고르는 값은 변형과
 * 완전히 같고(ai-variation-screens §3 강도·§4 정답유형), 다른 점은 하나뿐 — 이 강도가 원본
 * 문항 '전부'에 똑같이 걸린다. 그래서 문항 수를 전면에 둔다: 누르는 순간 몇 건이 돌기
 * 시작하는지가 여기서만 알 수 있는 사실이다.
 *
 * 여기서는 시작만 하고 닫는다. 진행·문항별 검수·조립은 배치 화면(BatchPanel)이 맡는다.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { Modal, Button, AiPromptInput } from '../common';
import { useAuth } from '../../context/AuthContext';
import { LEVELS, ANSWER_TYPE_OPTIONS } from '../ProblemHubV2/variationApi';
import { startAiCopy, AUTH_REDIRECT } from './examPaperApi';

/** 문항 수 키가 계약 문구('문항수')로만 있다 — 요약·상세 어느 판으로 와도 읽는다. 모르면 null(짐작하지 않는다) */
function itemCountOf(paper) {
  if (Array.isArray(paper?.items)) return paper.items.length;
  return paper?.item_count ?? paper?.items_count ?? paper?.problem_count ?? null;
}

function AiCopyModal({ isOpen, paper, onClose, onStarted = null }) {
  const { getAccessToken } = useAuth();
  const [level, setLevel] = useState(2); // 단건 변형과 같은 기본값 — 원본 구조를 지키면서 쓸모가 있는 지점
  // AI 에 함께 보낼 지시(선택). 이 화면은 이미 모달이라 창을 겹치지 않고 칸만 둔다.
  const [userPrompt, setUserPrompt] = useState('');
  const [advOpen, setAdvOpen] = useState(false);
  const [optDifficulty, setOptDifficulty] = useState('keep');
  const [optAnswerType, setOptAnswerType] = useState('keep');
  const [title, setTitle] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const paperId = paper?.paper_id ?? paper?.id ?? null;
  const paperTitle = paper?.title ?? '';
  const itemCount = itemCountOf(paper);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true; // StrictMode 재마운트에서 false 로 굳지 않게 열 때마다 세운다
    return () => { mountedRef.current = false; };
  }, []);

  // 열 때마다 처음 상태로 — 앞서 취소한 시험지의 제목·에러가 다음 시험지에 남으면 그대로 전송된다.
  // 지시 칸도 함께 비운다: 이 저장소의 지시 칸 규약 ②(AiRunModal.jsx 머리 주석 「열 때마다 비운다 —
  // 지난 요청의 지시가 남아 다른 대상에 실리면 조용히 엉뚱한 것이 만들어진다」)다. 이 모달만 공용
  // AiRunModal 을 안 쓰고 AiPromptInput 을 직접 붙여, 그때 이 한 줄이 따라오지 않았다.
  // 여기서 새는 것은 특히 비싸다 — 지시가 시험지 **전 문항**에 걸리고 배치는 되돌릴 수 없다.
  useEffect(() => {
    if (!isOpen) return;
    setTitle(paperTitle ? `${paperTitle} (AI 복사)` : '');
    setUserPrompt('');
    setError(null);
    setPending(false);
  }, [isOpen, paperId, paperTitle]);

  // 다이얼로그 시맨틱(role·aria-modal·aria-labelledby)과 초점 가두기·되돌리기는 셸(Modal)이
  // 모두 맡는다 — 여기서 겹쳐 두면 다이얼로그가 두 겹이 되고 초점이 두 번 옮겨진다.

  // 요청 중에는 ESC·배경·[X] 어느 길로도 닫히지 않는다 — 닫아도 요청은 이미 서버로 갔고,
  // 그러면 배치 번호를 받을 곳이 사라져 진행 중인 배치를 찾아갈 방법이 없어진다.
  const requestClose = () => {
    if (pending) return;
    onClose();
  };

  const submit = async () => {
    if (pending || paperId == null) return;
    setPending(true);
    setError(null);
    try {
      const options = {};
      if (optDifficulty !== 'keep') options.difficulty = optDifficulty;
      if (optAnswerType !== 'keep') options.answer_type = optAnswerType;
      const d = await startAiCopy(paperId, { level, options, title: title.trim(), userPrompt }, getAccessToken);
      // 401 이면 쓰기 헬퍼가 /signin 으로 보내며 null 을 돌려준다 — 배치 번호 탓으로 돌리지 않는다
      if (d === AUTH_REDIRECT) return;
      const batchId = d?.batch_id ?? d?.id ?? null;
      if (batchId == null) throw new Error('배치 번호를 받지 못했습니다 — 시험지 목록에서 진행 상태를 확인해 주세요.');
      onStarted && onStarted(batchId);
      onClose();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message || 'AI 복사를 시작하지 못했습니다.');
    } finally {
      if (mountedRef.current) setPending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={requestClose}
      title="AI 복사 시험지 만들기"
      size="lg"
      maxHeight="88vh"
      closeOnEsc={!pending}
      closeOnBackdropClick={!pending}
    >
      <div className="space-y-4">
        {/* 이 배치가 무엇을 건드리는지 — 강도를 고르기 전에 규모를 먼저 본다 */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="truncate text-sm font-bold text-gray-800">{paperTitle || `시험지 #${paperId ?? '—'}`}</p>
          <p className="mt-1 text-xs leading-relaxed text-gray-600">
            {itemCount != null
              ? <>원본 <b>{itemCount}문항 전부</b>를 같은 강도로 한 번에 변형합니다.</>
              : <>원본 문항 <b>전부</b>를 같은 강도로 한 번에 변형합니다.</>}
            {' '}문항별로 강도를 다르게 줄 수는 없습니다 — 원본은 그대로 두고 새 시험지가 따로 생깁니다.
          </p>
          <p className="mt-1 text-[11px] text-gray-500">
            생성은 문항당 수십 초 걸리고, 만들어진 문항은 하나씩 검수해 승인·폐기한 뒤 조립해야 시험지가 됩니다.
          </p>
        </div>

        {/* 강도 — 전면(계약 §3) */}
        <div>
          <div className="mb-2 flex items-center gap-1.5">
            <Sparkles aria-hidden="true" className="size-4 text-violet-500" />
            <span className="text-sm font-bold text-gray-800">변형 강도</span>
            <span className="text-[11px] text-gray-400">아래로 갈수록 원본에서 멀어집니다</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4" role="group" aria-label="변형 강도">
            {LEVELS.map((l) => (
              <button
                key={l.value}
                type="button"
                onClick={() => setLevel(l.value)}
                aria-pressed={level === l.value}
                title={l.hint}
                className={`rounded-lg border px-2 py-1.5 text-left transition-colors ${
                  level === l.value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary/40'
                }`}
              >
                <span className={`block text-xs font-bold ${level === l.value ? 'text-primary' : 'text-gray-700'}`}>
                  L{l.value} {l.label}
                </span>
                <span className="mt-0.5 block text-[10px] leading-tight text-gray-400">{l.hint}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 고급 — 단건 변형과 같은 옵션 집합(§3 고급 · §4-1 정답유형) */}
        <div>
          <button
            type="button"
            onClick={() => setAdvOpen((v) => !v)}
            aria-expanded={advOpen}
            aria-controls="ai-copy-adv"
            className="flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700"
          >
            {advOpen ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            고급 — 난이도·문제 유형
          </button>
          {advOpen && (
            <div id="ai-copy-adv" className="mt-1.5 space-y-1.5 rounded-lg bg-gray-50 p-2">
              <label className="flex items-center gap-2 text-[11px] text-gray-600">
                <span className="w-16 shrink-0">난이도</span>
                <select
                  value={optDifficulty}
                  onChange={(e) => setOptDifficulty(e.target.value)}
                  disabled={pending}
                  className="min-w-0 flex-1 rounded border px-1.5 py-1 text-xs focus:border-primary focus:outline-none disabled:bg-gray-100"
                >
                  <option value="keep">유지</option>
                  <option value="up">한 단계 올림</option>
                  <option value="down">한 단계 내림</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-[11px] text-gray-600">
                <span className="w-16 shrink-0">문제 유형</span>
                <select
                  value={optAnswerType}
                  onChange={(e) => setOptAnswerType(e.target.value)}
                  disabled={pending}
                  className="min-w-0 flex-1 rounded border px-1.5 py-1 text-xs focus:border-primary focus:outline-none disabled:bg-gray-100"
                >
                  {ANSWER_TYPE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </label>
              <p className="text-[10px] text-gray-400">
                두 옵션은 AI에게 주는 유도값이라 100% 보장되지 않습니다 — 결과는 문항별 검수에서 확인하세요.
                한 배치의 모든 문항에 같은 값이 걸립니다.
              </p>
            </div>
          )}
        </div>

        {/* 새 시험지 제목 */}
        <div>
          <label htmlFor="ai-copy-title-input" className="mb-1 block text-xs font-medium text-gray-500">
            새 시험지 제목
          </label>
          <input
            id="ai-copy-title-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={pending}
            placeholder={paperTitle ? `${paperTitle} (AI 복사)` : '새 시험지 제목'}
            aria-describedby="ai-copy-title-hint"
            className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
          />
          <p id="ai-copy-title-hint" className="mt-1 text-[10px] text-gray-400">
            조립할 때 이 제목으로 새 시험지가 생깁니다. 비워 두면 백엔드 기본 제목이 붙습니다.
          </p>
        </div>

        {/* 지시는 문항 전부에 걸린다 — 한 장의 모든 문항이 같은 말을 받는다는 것을 안내에 적었다 */}
        <AiPromptInput
          label="AI 에 보낼 지시(선택)"
          value={userPrompt}
          onChange={setUserPrompt}
          rows={2}
          placeholder="예: 수치만 바꾸고 문장은 그대로 · 그래프는 유지"
          disabled={pending}
          /* 「옵션·규칙이 지시를 이긴다」 안내를 켠다(BE-20260829-reply-fe-0828.md §4-2 5-1).
             이 자리는 켤 자격이 있다 — 위 submit 이 강도(level)와 고급 옵션(difficulty·answer_type)을
             지시와 **같은 요청 몸통에** 실어 보낸다. 여기가 셋 중 가장 비싸다: 같은 옵션이 시험지
             **전 문항**에 한 번에 걸려, 「쉽게」가 무시된 것을 배치가 다 돌고 난 뒤에 알면 되돌릴 수 없다. */
          showOptionPriority
        />
        <p className="-mt-1 text-[11px] text-gray-400">
          비워 두고 시작해도 됩니다. 쓰면 이 시험지의 <b>모든 문항</b>에 같은 지시가 걸립니다.
        </p>

        {/* 라이브 리전은 항상 마운트해 두고 문구만 바꾼다 — 내용과 함께 삽입되면 낭독이 불안정하다 */}
        <p
          role="alert"
          aria-live="assertive"
          className={`text-xs ${error ? 'text-danger' : 'sr-only'}`}
        >
          {error || ''}
        </p>

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <span className="text-[11px] text-gray-400">
            {pending ? '요청을 보내는 중입니다 — 닫지 마세요.' : '시작하면 배치 화면으로 넘어갑니다.'}
          </span>
          <span className="ml-auto" />
          <Button size="sm" variant="secondary" onClick={requestClose} disabled={pending}>취소</Button>
          <Button
            size="sm"
            onClick={submit}
            loading={pending}
            disabled={pending || paperId == null}
            icon={<Sparkles className="size-3.5" />}
          >
            {itemCount != null ? `${itemCount}문항 변형 시작` : 'AI 복사 시작'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default AiCopyModal;
